import Router from 'next/router';

export interface ApolloNetworkErrorCause {
  networkError?: { cause?: { graphQLOperationType?: string } };
}

let activeNavigations = 0;
let pageHidden = false;

type TeardownListener = () => void;
const listeners = new Set<TeardownListener>();

// Notify after the state is updated so listeners read the settled isTearingDown().
const notify = (): void => {
  listeners.forEach((listener) => listener());
};

export const subscribeToTeardown = (
  listener: TeardownListener
): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const isTearingDown = (): boolean => activeNavigations > 0 || pageHidden;

export const isTeardownNetworkError = (error: unknown): boolean => {
  if (!isTearingDown()) return false;
  const apolloLike = error as ApolloNetworkErrorCause;
  return Boolean(apolloLike?.networkError?.cause?.graphQLOperationType);
};

export const isTeardownAbortedQuery = (error: unknown): boolean => {
  if (!isTeardownNetworkError(error)) return false;
  const apolloLike = error as ApolloNetworkErrorCause;
  return apolloLike.networkError?.cause?.graphQLOperationType === 'query';
};

export const initTeardownSignal = (): (() => void) => {
  const handleRouteChangeStart = () => {
    activeNavigations += 1;
    notify();
  };
  const handleRouteChangeSettled = () => {
    activeNavigations = Math.max(0, activeNavigations - 1);
    notify();
  };
  const handleVisibilityChange = () => {
    pageHidden = document.visibilityState === 'hidden';
    notify();
  };
  const handlePageHide = () => {
    pageHidden = true;
    notify();
  };

  Router.events.on('routeChangeStart', handleRouteChangeStart);
  Router.events.on('routeChangeComplete', handleRouteChangeSettled);
  Router.events.on('routeChangeError', handleRouteChangeSettled);
  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('pagehide', handlePageHide);

  return () => {
    Router.events.off('routeChangeStart', handleRouteChangeStart);
    Router.events.off('routeChangeComplete', handleRouteChangeSettled);
    Router.events.off('routeChangeError', handleRouteChangeSettled);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('pagehide', handlePageHide);
    activeNavigations = 0;
    pageHidden = false;
  };
};
